import type {
  EnrollmentRequirement,
  EnrollmentRequirementInputType
} from '@/api/enrollment-requirements'

type RequirementValue = string | number | boolean

type PartnerRequirementFieldsProps = {
  requirements: EnrollmentRequirement[]
  values: Record<number, RequirementValue | undefined>
  onChange: (requirementId: number, value: RequirementValue) => void
}

const inputType = (type?: EnrollmentRequirementInputType | null) => {
  if (type === 'NUMBER') return 'number'
  if (type === 'DATE') return 'date'
  if (type === 'EMAIL') return 'email'
  if (type === 'PHONE') return 'tel'
  return 'text'
}

export function PartnerRequirementFields({
  requirements,
  values,
  onChange
}: PartnerRequirementFieldsProps) {
  if (requirements.length === 0) return null

  return (
    <section className="rounded-2xl border border-blue-200 bg-blue-50/60 p-5">
      <h2 className="text-lg font-bold text-gray-900">Partner information</h2>
      <p className="mt-1 text-sm text-gray-700">
        Your enrollment partner needs the details below. Anything already provided for you has been
        left out.
      </p>
      <div className="mt-4 grid gap-4 md:grid-cols-2">
        {requirements.map(requirement => {
          const value = values[requirement.id]
          const label = `${requirement.label}${requirement.required ? ' *' : ''}`

          return (
            <label key={requirement.id} className="block text-sm font-semibold text-gray-800">
              <span>{label}</span>
              {requirement.inputType === 'CHECKBOX' ? (
                <span className="mt-2 flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={value === true}
                    onChange={event => onChange(requirement.id, event.target.checked)}
                    className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
                  />
                  <span className="font-normal">Yes</span>
                </span>
              ) : requirement.inputType === 'TEXT_AREA' ? (
                <textarea
                  value={typeof value === 'string' ? value : ''}
                  onChange={event => onChange(requirement.id, event.target.value)}
                  rows={3}
                  className="mt-2 w-full rounded-lg border border-gray-300 bg-white px-3 py-2 font-normal focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
                />
              ) : (
                <input
                  type={inputType(requirement.inputType)}
                  value={typeof value === 'string' || typeof value === 'number' ? value : ''}
                  onChange={event =>
                    onChange(
                      requirement.id,
                      requirement.inputType === 'NUMBER'
                        ? event.target.valueAsNumber
                        : event.target.value
                    )
                  }
                  className="mt-2 w-full rounded-lg border border-gray-300 bg-white px-3 py-2 font-normal focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
                />
              )}
              {requirement.helpText ? (
                <span className="mt-1 block text-xs font-normal text-gray-600">
                  {requirement.helpText}
                </span>
              ) : null}
            </label>
          )
        })}
      </div>
    </section>
  )
}
